import { Base, withBreakpointObserver } from '@studiometa/js-toolkit';
import { gsap } from 'gsap';

/**
 * @typedef {{
 *   $refs: {
 *     items: HTMLElement[],
 *     pictures: HTMLElement[],
 *   }
 * }} ArchivesProps
 */

/**
 * Archives component.
 * Hover on items to reveal corresponding pictures,
 * with scroll-aware hover detection via elementFromPoint.
 * @augments {Base<ArchivesProps>}
 */
export default class Archives extends withBreakpointObserver(Base) {
  static config = {
    name: 'Archives',
    refs: ['items[]', 'pictures[]'],
    options: {
      inactiveBreakpoints: {
        default: 'xxs s',
        type: String,
      },
    },
  };

  /** @type {number} */
  activeIndex = -1;

  /** @type {{ x: number, y: number }} */
  mouse = { x: 0, y: 0 };

  /**
   * Track mouse position.
   * @param {{ event: MouseEvent }} args
   */
  onMousemove({ event }) {
    this.mouse.x = event.clientX;
    this.mouse.y = event.clientY;
  }

  /**
   * Re-evaluate hover on scroll.
   */
  scrolled() {
    if (!this.mouse.x && !this.mouse.y) return;

    const el = document.elementFromPoint(this.mouse.x, this.mouse.y);
    if (!el) return;

    const index = this.$refs.items.findIndex((item) => item === el || item.contains(el));

    if (index !== -1 && index !== this.activeIndex) {
      this.activate(index);
    }
  }

  /**
   * Handle mouseenter on an item ref.
   * @param {{ index: number }} args
   */
  onItemsMouseenter({ index }) {
    if (index !== this.activeIndex) {
      this.activate(index);
    }
  }

  /**
   * Activate item and picture at the given index.
   * @param {number} index
   */
  activate(index) {
    const prevIndex = this.activeIndex;
    this.activeIndex = index;

    this.$refs.items.forEach((item, i) => {
      gsap.to(item, {
        opacity: i === index ? 1 : 0.3,
        duration: 0.3,
        overwrite: true,
      });
    });

    if (this.$refs.pictures[prevIndex]) {
      gsap.set(this.$refs.pictures[prevIndex], { opacity: 0 });
    }

    if (this.$refs.pictures[index]) {
      gsap.set(this.$refs.pictures[index], { opacity: 1 });
    }
  }

  /**
   * Reset state on mouseleave.
   */
  onMouseleave() {
    if (this.activeIndex !== -1) {
      gsap.set(this.$refs.pictures[this.activeIndex], { opacity: 0 });
    }
    this.activeIndex = -1;

    this.$refs.items.forEach((item) => {
      gsap.to(item, {
        opacity: 0.3,
        duration: 0.3,
        overwrite: true,
      });
    });
  }
}
